import React, { useState, useEffect } from 'react';
import { Check, BookOpen, Star, Download, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardFooter } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Book = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [bookPkg, setBookPkg] = useState({ amount: 29.99, name: 'Dad Mode: Activated Book' });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    fetchPackage();
  }, []);

  const fetchPackage = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/packages`);
      if (response.data.book) {
        setBookPkg(response.data.book);
      }
    } catch (error) {
      console.error('Error fetching package:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    setIsProcessing(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/payments/checkout`, {
        package_id: 'book',
        origin_url: window.location.origin,
        user_email: user?.email || null
      });
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to start checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  const defaultFeatures = [
    '150+ carefully curated activities for all age groups',
    'Detailed step-by-step instructions for every activity',
    'Printable activity cards for quick reference',
    'Bonus section: Building lifelong skills',
    'Tips for adapting activities to your child\'s interests',
    'Resource lists organized by category',
    'Expert insights from 20+ years of youth work',
    'Beautiful full-color illustrations'
  ];

  const bookFeatures = bookPkg.features || defaultFeatures;

  const preview = [
    {
      chapter: 'Chapter 1: The Foundation Years (0-5)',
      activities: 'Tummy Time Adventures, Sensory Exploration, First Words Games, Messy Play Magic'
    },
    {
      chapter: 'Chapter 2: The Explorer Years (6-10)',
      activities: 'Backyard Science, Basic Woodworking, Cooking Together, Nature Journals'
    },
    {
      chapter: 'Chapter 3: The Identity Years (11-13)',
      activities: 'Photography Projects, Basic Mechanics, Budgeting Games, Social Skills'
    },
    {
      chapter: 'Chapter 4: The Independence Years (14-18)',
      activities: 'Career Exploration, Financial Planning, Car Maintenance, Life Skills Mastery'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-green-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_dadtime-guides/artifacts/68md2mqj_Dad%20Mode%20Book.png" 
            alt="Dad Mode: Activated Book Cover" 
            className="mx-auto h-80 md:h-96 w-auto mb-6 shadow-2xl rounded-lg"
          />
          <h1 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-green-600 bg-clip-text text-transparent">
              Dad Mode: Activated
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-medium">
            Everything you need to build awesome connections with your children, all in one super fun guide!
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Book Preview/Description */}
          <div>
            <Card className="border-2 mb-8">
              <CardHeader>
                <h2 className="text-3xl font-bold">What's Inside</h2>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-gray-700 leading-relaxed">
                  This isn't just another parenting book filled with theory. It's a practical, hands-on guide packed 
                  with real activities you can do today to strengthen your bond with your children.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Whether you have a newborn or a teenager, you'll find age-appropriate activities that teach valuable 
                  skills while creating memories that last a lifetime.
                </p>

                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-900">What Makes This Book Different?</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Written by a father with 20+ years of youth work experience</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Real activities tested with real families</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Organized by age group for easy navigation</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Includes both fun activities AND life skills</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Chapter Preview */}
            <Card className="border-2">
              <CardHeader>
                <h3 className="text-2xl font-bold">Chapter Preview</h3>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {preview.map((item, index) => (
                    <div key={index} className="pb-4 border-b last:border-b-0">
                      <h4 className="font-bold text-gray-900 mb-2">{item.chapter}</h4>
                      <p className="text-gray-600 text-sm">{item.activities}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pricing Card */}
          <div>
            <Card className="border-4 border-teal-300 shadow-2xl sticky top-24">
              <CardHeader className="bg-gradient-to-br from-blue-500 via-teal-500 to-green-500 text-white rounded-t-lg">
                <div className="text-center py-6">
                  <div className="flex justify-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-6 w-6 fill-yellow-300 text-yellow-300" />
                    ))}
                  </div>
                  <h3 className="text-3xl font-black mb-2">{bookPkg.name || 'Complete Activity Guide'}</h3>
                  <p className="text-white/90 mb-4 font-medium">{bookPkg.description || 'Digital + Printable PDF'}</p>
                  <div className="flex items-baseline justify-center gap-2">
                    {bookPkg.original_price && (
                      <span className="text-2xl text-white/60 line-through">${bookPkg.original_price}</span>
                    )}
                    <span className="text-5xl font-black">${bookPkg.amount}</span>
                  </div>
                  {bookPkg.badge_text && (
                    <p className="text-white/90 text-sm mt-2 font-bold">{bookPkg.badge_text}</p>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-8">
                <h4 className="font-bold text-lg text-gray-900 mb-4">What You Get:</h4>
                <ul className="space-y-3 mb-8">
                  {bookFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-gradient-to-r from-green-50 to-teal-50 border-2 border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Download className="h-6 w-6 text-green-600" />
                    <div>
                      <p className="font-bold text-gray-900">Instant Digital Access</p>
                      <p className="text-sm text-gray-600">Download immediately after purchase</p>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="p-8 pt-0">
                <Button 
                  size="lg" 
                  className="w-full text-lg py-6 bg-gradient-to-r from-blue-500 via-teal-500 to-green-500 hover:from-blue-600 hover:via-teal-600 hover:to-green-600 text-white shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-200 font-black"
                  onClick={handlePurchase}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <BookOpen className="h-5 w-5 mr-2" />
                      Get the Book - ${bookPkg.amount}
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>

        {/* Reviews Section */}
        <Card className="border-2 mb-12">
          <CardHeader className="text-center">
            <h3 className="text-2xl font-bold">What Dads Are Saying</h3>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  name: 'Mike R.',
                  text: 'Finally, a parenting book that gets straight to the point! My kids and I have already tried 10 activities.',
                  stars: 5
                },
                {
                  name: 'James T.',
                  text: 'The age-specific organization is brilliant. I can quickly find activities perfect for my 7-year-old.',
                  stars: 5
                },
                {
                  name: 'David L.',
                  text: 'Worth every penny. The life skills section alone has transformed how I prepare my teens for adulthood.',
                  stars: 5
                }
              ].map((review, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex mb-2">
                    {[...Array(review.stars)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm mb-2">"{review.text}"</p>
                  <p className="text-gray-900 font-bold text-sm">- {review.name}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <div>
          <h2 className="text-3xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: 'What format is the book?',
                a: 'You\'ll receive a high-quality PDF that works on any device. Print it, read it on your tablet, or view it on your computer.'
              },
              {
                q: 'Is this book good for all ages?',
                a: 'Absolutely! The book is organized by age groups from newborn through 18 years old. You\'ll find relevant activities no matter your children\'s ages.'
              },
              {
                q: 'How quickly can I access the book?',
                a: 'Instantly! After purchase, you\'ll receive immediate access to download the PDF.'
              },
              {
                q: 'Do I need special supplies for the activities?',
                a: 'Most activities use common household items. Each activity lists any materials needed, and we\'ve made sure to keep requirements simple and affordable.'
              }
            ].map((faq, index) => (
              <Card key={index} className="border-2">
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{faq.q}</h3>
                  <p className="text-gray-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Book;
